const MediaProxy = require('./media-proxy');

const SOCIAL_MEDIA_CACHE_CDN_BASE_URL = (process.env.SOCIAL_MEDIA_CACHE_CDN_BASE_URL)
  ? process.env.SOCIAL_MEDIA_CACHE_CDN_BASE_URL
  : 'https://d9wxf5clf37aa.cloudfront.net';

/**
 * Determines if the supplied url is already social media cache cdn.
 * @param {String} url
 * @param {String} network
 * @param {String} postId - The external id of the social network post.
 * @returns {boolean}
 */
function urlIsCached(url, network, postId) {
  return !!(url && url.length && url.includes(`/${network}/${postId}/`));
}

/**
 * Determines if a feed thumbnail may still exist in the cache or if it has been expired.
 * Note: Feed thumbnails are expired after 60 days. Reference: https://github.com/Tagboard/infrastructure/blob/790a00427053d70e15c9c7f3594646c858ef0c54/core/s3/social-media-cache.ts#L71-L79
 * @param {Number} thumbnailCached - The timestamp (in milliseconds) of when a feed thumbnail was cached.
 * @returns {boolean}
 */
function thumbnailIsStillCached(thumbnailCached) {
  if (!(thumbnailCached > 0)) {
    return false;
  }
  const SixtyDaysInMs = 5184e6;
  return thumbnailCached > (new Date().getTime() - SixtyDaysInMs);
}

/**
 * Determines if a featured/curated post has cached media.
 * @param {Object} post
 * @param {Number} post.mediaCached
 * @param {String} post.post_type
 * @returns {boolean}
 */
function hasInstagramCachedMediaUrl(post) {
  const { post_type: postType, mediaCached } = post || {};
  return !!mediaCached && ['photo', 'video'].includes(postType);
}

/**
 * Returns the cached media url from a post.
 * @param {Object} post - An Instagram post from the Story api.
 * @param size
 * @returns {string|null}
 */
function instagramCachedMediaUrl(post, size) {
  const { post_type: postType, videos } = post || {};

  if (size === 'video' && postType === 'video' && (videos && videos.length)) {
    return videos[0].m;
  }

  let cachedMediaUrl;
  const mediaObject = post[`${postType}s`][0] || {};

  if (size === 't') {
    cachedMediaUrl = (postType === 'photo')
      ? mediaObject.s // small photo
      : mediaObject.t; // video thumbnail
  }

  if (size === 'm') {
    cachedMediaUrl = (postType === 'video')
      ? mediaObject.m // video file
      : mediaObject.m; // medium photo
  }

  if (size === 'l') {
    cachedMediaUrl = (postType === 'video')
      ? mediaObject.p // video poster image
      : mediaObject.l; // large photo
  }

  if (!cachedMediaUrl && mediaObject[size]) {
    cachedMediaUrl = mediaObject[size];
  }

  return cachedMediaUrl; // It's fine if this falls back to a non-cached url, that can be handled by the invoking method.
}

function hasInstagramCachedFeedThumbnail(post, size) {
  const { post_type: postType, thumbnailCached } = post || {};
  const hasCachedFeedMedia = thumbnailCached && thumbnailIsStillCached(thumbnailCached);
  const thumbnailCachedPhoto = hasCachedFeedMedia && postType === 'photo' && size === 's';
  const thumbnailCachedVideo = hasCachedFeedMedia && postType === 'video' && size === 't';
  return thumbnailCachedPhoto || thumbnailCachedVideo;
}

/**
 * Determines the implicit Tagboard CDN url path for a "Feed" post thumbnail.
 * Note: Assumes implicit Tagboard CDN url path as Bottle Brush tends to overwrite the CDN url during updates.
 * @param post
 * @returns {string}
 */
function instagramCachedFeedThumbnail(post) {
  const { post_id: postId, post_type: postType } = post || {};
  const fileName = (postType === 'video') ? 'videoThumbnail' : 'small';
  return `${SOCIAL_MEDIA_CACHE_CDN_BASE_URL}/feed/instagram/${postId}/${fileName}.jpeg`;
}

function getInstagramCdnMediaSize(requestedSize) {
  const validIgCdnMediaSizes = [
    'l', // Full sized original image
    'm', // Medium preview/poster image (320px wide)
    't', // Small thumbnail image. (150px x 150px)
  ];

  if (validIgCdnMediaSizes.includes(requestedSize)) {
    return requestedSize;
  }

  if (requestedSize === 's') {
    return 't';
  }

  return 'l';
}

function instagramCdnMediaUrl(post, size, skipProxy = false) {
  const {
    permalink,
    post_type: postType,
    service_meta: serviceMeta,
    videos,
  } = post || {};
  const { shortcode } = serviceMeta || {};

  // Generate a url to the IG endpoint that resolves to the current IG CDN media url
  const baseUrl = shortcode ? `https://www.instagram.com/p/${shortcode}/` : permalink;
  const mediaSize = getInstagramCdnMediaSize(size);
  let igMediaUrl = `${baseUrl}media/?size=${mediaSize}`;

  if (size === 'video' && postType === 'video' && (videos && videos.length)) {
    igMediaUrl = videos[0].m; // Return stored video url as the IG endpoint does not support actual videos
  }

  if (skipProxy === true) {
    return igMediaUrl;
  }

  return MediaProxy.genUrl(igMediaUrl);
}

/**
 * Returns an Instagram "Media" proxy url that resolves to the current CDN url for the media.
 * Note: Only works for the first image/video on a post.
 * Note: For video posts it only returns the still preview images, not the video itself.
 * @param {Object} post - An Instagram post from the Story api.
 * @param {"l" | "m" | "t" | "s" | "p" | "video"} [size] - Size of the media. Defaults to the Full-Sized original.
 * @param {Boolean} [skipProxy] - Returns the raw, non-proxied URL. Instagram urls will throw CORS errors the proxy.
 * @returns {string|undefined}
 */
function instagramMediaUrl(post, size, skipProxy = false) {
  const { network, post_id: postId } = post || {};
  if (network !== 'instagram') {
    throw new Error(`post_id: ${postId} ${network} is not an Instagram post`);
  }

  // Featured/Curated Post Cached Media
  if (hasInstagramCachedMediaUrl(post)) {
    const cachedMediaUrl = instagramCachedMediaUrl(post, size);
    if (urlIsCached(cachedMediaUrl, network, postId)) {
      return cachedMediaUrl;
    }
  }

  // Feed Post Cached Thumbnails
  if (hasInstagramCachedFeedThumbnail(post, size)) {
    return instagramCachedFeedThumbnail(post);
  }

  // Fall back to the Instagram CDN
  return instagramCdnMediaUrl(post, size, skipProxy);
}

module.exports = {
  instagramMediaUrl,
  instagramCachedMediaUrl,
};
